// src/components/homepage/HomePageFinalCta.tsx

import React from 'react';
import { motion } from 'framer-motion';
import { Rocket, MessageSquarePlus, Users } from 'lucide-react';
import {Button} from '@dopomogai/ui';

// Interface matching the homepage.json structure
interface FinalCtaData {
  headline: string;
  text: string;
  button1: string;
  button2: string;
  link1: string;
  link2: string;
  link3: string;
}

interface HomePageFinalCtaProps {
  finalCtaData: FinalCtaData;
  onAction: (actionType: string, payload?: any) => void;
  id?: string;
}

export const HomePageFinalCta: React.FC<HomePageFinalCtaProps> = ({ finalCtaData, onAction, id }) => {

  const linkVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1 + 0.6, // Stagger links after buttons
        duration: 0.5,
        ease: 'easeOut',
      },
    }),
  };

  return (
    <section id={id} className="py-16 md:py-24">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.95 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: [0.25, 1, 0.5, 1] }}
          className="bg-gray-800 text-white p-8 md:p-12 rounded-2xl border-2 border-black text-center"
        >
          <div className="inline-flex items-center justify-center p-3 bg-white/10 rounded-lg border border-white/20 mb-5">
            <Rocket size={32} className="text-white" />
          </div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.8 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-3xl md:text-4xl lg:text-5xl font-bold"
          >
            {finalCtaData.headline}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.8 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mt-6 max-w-3xl mx-auto text-lg md:text-xl text-gray-300 leading-relaxed"
          >
            {finalCtaData.text}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mt-10 md:mt-12 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-5"
          >
            <Button
              text={finalCtaData.button1}
              variant="primary"
              size="lg"
              icon={<MessageSquarePlus size={20} />}
              iconPosition="left"
              onClick={() => onAction('OPEN_CONTACT_MODAL', { type: 'audit' })}
              // Light green shadow for primary button on dark background
              className="hover:shadow-[4px_4px_0_0_#6ee7b7]" // emerald-300
            />
            <Button
              text={finalCtaData.button2}
              variant="secondary"
              size="lg"
              icon={<Users size={20} />}
              iconPosition="left"
              onClick={() => onAction('OPEN_WAITLIST_MODAL')}
              // Light gray shadow for secondary button on dark background
              className="bg-slate-200 text-slate-800 hover:bg-white hover:shadow-[4px_4px_0_0_#94a3b8]" // slate-400
            />
          </motion.div>

          <div className="mt-10 flex flex-wrap justify-center gap-x-6 sm:gap-x-8 gap-y-3 text-sm">
            {[
              { text: finalCtaData.link1, path: '/#assessment' },
              { text: finalCtaData.link2, path: '/#assessment' },
              { text: finalCtaData.link3, path: '/#assessment' },
            ].map((link, index) => (
              <motion.div
                key={link.text}
                custom={index}
                variants={linkVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <a
                  href={link.path}
                  onClick={(e) => {
                    e.preventDefault();
                    onAction('NAVIGATE_TO_PAGE', { path: link.path });
                  }}
                  className="text-gray-400 hover:text-white hover:underline focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2 focus:ring-offset-gray-800 rounded-sm transition-colors duration-200"
                >
                  {link.text}
                </a>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

